import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    QueryResponse,
    SortDirections,
    FilterMetadata,
} from "~/modules/filters/types";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_SORT,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import {
    ASSET_CLASS_IDS,
    MEASURE_IDS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
    DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
    DISTRIBUTION_TYPE_TOOLTIPS,
} from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { AggregateValueTypes } from "~/components/AggregateDisplay/types";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { ColumnMetadata } from "~/components/DataGrid";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { NET_ASSET_VALUE_COLUMN } from "~/modules/assets/components/AssetTable/columns/NetAssetValue";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import {
    SEVEN_DAYS_AGO,
    THIRTY_DAYS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { getTopTransfers } from "~/modules/transactions/api/getTopTransfers";
import { TopTransfersData } from "~/modules/transactions/components/TopTransfersCard/types";
import { TransactionColumns } from "~/modules/transactions/types";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { MANAGER_COLUMN } from "~/modules/assets/components/AssetTable/columns/Manager";
import {
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/Performance";
import { SECTORS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Sectors";
import { TOKENIZATION_TYPE_COLUMN } from "~/modules/assets/components/AssetTable/columns/TokenizationType";
import { BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenValueDollar";
import {
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
} from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { MANAGEMENT_FEE_COLUMN } from "~/modules/assets/components/AssetTable/columns/ManagementFee";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Tokenized Active Strategies";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.active_strategies;

// // // //

const ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS: Filter[] = [
    {
        field: TransactionColumns.assetClassID,
        operator: FilterOperators.Equals,
        value: ASSET_CLASS_IDS.ActiveStrategies,
    },
];

const ACTIVE_STRATEGIES_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE,
    MANAGER_COLUMN,
    BRIDGED_TOKEN_VALUE_DOLLAR_COLUMN,
    NET_ASSET_VALUE_COLUMN,
    PERFORMANCE_1_MONTH_COLUMN,
    PERFORMANCE_1_YEAR_COLUMN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    { ...SECTORS_COLUMN, width: 140 },
    TOKENIZATION_TYPE_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    PRIMARY_MARKET_ALLOWED_INVESTORS_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    MANAGEMENT_FEE_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

const ACTIVE_STRATEGIES_ASSETS_QUERY: ApiQuery = {
    filter: ASSET_CLASS_PAGES_TO_FILTER.active_strategies,
    sort: ASSET_CLASS_PAGE_TO_SORT.active_strategies,
    pagination: {
        page: 1,
        perPage: 25,
    },
};

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Base filters for all aggregate queries
    const baseFilters: Filter[] =
        ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.active_strategies;

    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        netFlowsData,
        leagueTableData,
        distributedAggregates,
        representedAggregates,
        holdersData,
        // Top Transfers
        transactions_7d_under100k,
        transactions_7d_under1m,
        transactions_7d_under10m,
        transactions_7d_under_100m,
        transactions_30d_under100k,
        transactions_30d_under1m,
        transactions_30d_under10m,
        transactions_30d_under_100m,
    ] = await Promise.all([
        queryAssets({
            query: {
                ...ACTIVE_STRATEGIES_ASSETS_QUERY,
                name: "asset-class--active-strategies",
            },
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.active_strategies,
        }),
        queryNetFlows({
            name: "asset-class--active-strategies",
            filters:
                ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.active_strategies,
            groupBy: TokenTimeseriesGroupBy.asset,
            limit: 10,
        }),
        queryLeagueTablesByDistributionType({
            name: "asset-class--active-strategies",
            filter: {
                operator: FilterOperators.And,
                filters: baseFilters,
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.jurisdictionCountries,
            ],
        }),
        // 1. Distributed Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--active-strategies--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
            ],
        }),
        // 2. Represented Asset Value
        queryTokenAggregateValueWith30D({
            name: "asset-class--active-strategies--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),
        // 3. Holders
        queryTokenAggregateValueWith30D({
            name: "asset-class--active-strategies--total-holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
        // // // //
        // Top Transfers
        // 7-day transactions
        getTopTransfers({
            name: "asset-class--active-strategies--7d-under-100k",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--7d-under-1m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--7d-under-10m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: SEVEN_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--7d-under-100m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            timestamp: SEVEN_DAYS_AGO,
        }),
        // // // //
        // 30-day transactions
        getTopTransfers({
            name: "asset-class--active-strategies--30d-under-100k",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 100_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--30d-under-1m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 1_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--30d-under-10m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            max: 10_000_000,
            timestamp: THIRTY_DAYS_AGO,
        }),
        getTopTransfers({
            name: "asset-class--active-strategies--30d-under-100m",
            filters: ACTIVE_STRATEGIES_TOP_TRANSFERS_FILTERS,
            timestamp: THIRTY_DAYS_AGO,
        }),
    ]);

    // // // //

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Distributed Value",
            value: distributedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.distributed,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: distributedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Represented Value",
            value: representedAggregates.value,
            type: AggregateValueTypes.dollarCompact,
            tooltip: DISTRIBUTION_TYPE_TOOLTIPS.represented,
            tooltipLinkTo: DISTRIBUTION_TYPE_AGGREGATE_TOOLTIP_LINK_TO,
            percentChange: {
                value: representedAggregates.change30D,
                interval: "30d",
            },
        },
        {
            label: "Assets",
            value: listQueryResponse.results.length,
            type: AggregateValueTypes.count,
        },
        {
            label: "Holders",
            value: holdersData.value,
            type: AggregateValueTypes.count,
            percentChange: {
                value: holdersData.change30D,
                interval: "30d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata.filter((each) => {
        return [AssetColumns.networkIDs, AssetColumns.protocolIDs].includes(
            each.field as AssetColumns,
        );
    });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableData: leagueTableData,
            aggregates,
            netFlows: netFlowsData,
            topTransfers: {
                seven_day: {
                    under_100k: transactions_7d_under100k,
                    under_1m: transactions_7d_under1m,
                    under_10m: transactions_7d_under10m,
                    under_100m: transactions_7d_under_100m,
                },
                thirty_day: {
                    under_100k: transactions_30d_under100k,
                    under_1m: transactions_30d_under1m,
                    under_10m: transactions_30d_under10m,
                    under_100m: transactions_30d_under_100m,
                },
            },
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function ActivelyManagedStrategiesPage(props: {
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    aggregates: Array<AggregateDisplayProps>;
    leagueTableData: LeagueTableTabsByDistributionType;
    netFlows: NetFlowsData;
    topTransfers: TopTransfersData;
}) {
    const {
        leagueTableData,
        listQueryResponse,
        filterMetadata,
        netFlows,
        aggregates,
        topTransfers,
    } = props;

    const modes = getTimeseriesModes({
        analyticsKey: "actively-managed-strategies",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.active_strategies,
    });

    return (
        <PageTrack>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.activeStrategies)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.activeStrategies),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "tokenized active strategies, managed funds, hedge fund tokens, basis trades, dynamic strategies, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.activeStrategies}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.activeStrategies,
                    },
                ]}
            />
            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.activeStrategies}>
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        netFlows={{
                            title: "Tokenized Active Strategies Net Flows",
                            data: netFlows,
                        }}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={ACTIVE_STRATEGIES_ASSETS_QUERY}
                        header={{
                            label: "Tokenized Active Strategies",
                            loading: false,
                            value: `${listQueryResponse.results.length.toLocaleString()} Total`,
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Tokenized Active Strategies Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: true,
                                },
                                represented: {
                                    enabled: true,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: false,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            title: "Tokenized Active Strategies Value",
                            defaultGroupBy: LeagueTableGroupTypes.networks,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.platforms,
                                LeagueTableGroupTypes.managers,
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.jurisdictionCountries,
                            ],
                            leagueTableData,
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        topTransfers={{
                            title: "Tokenized Active Strategies Latest Transactions",
                            topTransfers: topTransfers,
                        }}
                        leagueTable={{
                            leagueTableData,
                            analyticsTableName:
                                "active-strategies-league-table",
                            title: "Tokenized Active Strategies League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: ACTIVE_STRATEGIES_TABLE_COLUMNS,
                            analyticsTableName: "active-strategies",
                            exportFilename: "active-strategies-assets",
                            sortQuery: {
                                field: AssetColumns.bridgedTokenValueDollar,
                                direction: SortDirections.desc,
                            },
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
